import type { SiteEntity } from '../../types/site';
import type { SiteDocument } from '../../types/siteRag';
import type { PublicSentimentEntry, SiteEvidenceDocument } from '../../types/siteEvidence';
import { docsEligibleForEvidence } from '../evidenceDocEligibility';
import { collectSedDocumentFacts } from '../sed/documentFacts';
import { documentBodyText } from '../documentBody';
import { hashCanonicalJson, sha256Hex } from '../reportGeneration/reportContractHash';
import { demonstrationDocumentProvenanceV1Schema } from '../../types/demonstration';
import { explicitProjectStance, scopeSentimentEntry, type SentimentQueryContext } from './publicSentimentScope';

const SHA = /^[a-f0-9]{64}$/;
const key = (text: string): string => text.replace(/\s+/g, ' ').trim().toLowerCase();
const publicRole = /\b(?:resident|citizen|neighbou?r|community representative|public commenter|local business owner)\b/i;
const foreignScope = /\b(?:another|different|other)\s+(?:project|site|parcel)\b|\b(?:do|does|did) not (?:address|apply to|describe|concern) this (?:project|site|parcel)\b|\bnot (?:for|about) this (?:project|site|parcel)\b/i;

function namesCurrentProject(text: string, name: string): boolean {
  const escaped = key(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Normal topic extraction may conservatively retain unknown project scope.
  // Only this literal actor-statement relation supplies local PS scope; a name
  // in a cover page, comparison, or negated statement does not.
  const statement = new RegExp(`\\b(?:stated|testified|commented)\\s+(?:for|about|regarding|on)\\s+${escaped}(?=\\s|[.,:;]|$)`, 'i');
  const source = key(text);
  const match = statement.exec(source);
  if (!match || foreignScope.test(source)) return false;
  const before = source.slice(0,match.index);
  const clauseStart = Math.max(before.lastIndexOf('.'),before.lastIndexOf('!'),before.lastIndexOf('?'),before.lastIndexOf(';')) + 1;
  return !/\b(?:not|never|no|denies?|denied)\b/.test(before.slice(clauseStart));
}

function namesLocalActor(text: string, attribution: NonNullable<PublicSentimentEntry['documentSource']>['attribution'], localities: readonly string[]): boolean {
  const escape = (value: string) => key(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const actor = escape(attribution.statedBy ?? '');
  const role = publicRole.exec(attribution.statedByRole ?? '')?.[0];
  if (!actor || !role) return false;
  return localities.some(locality => key(locality).length >= 3 && new RegExp(
    `(?:${actor}[,\\s]+(?:a\\s+)?${escape(locality)}\\s+${escape(role)}\\b|${actor}[,\\s]+(?:a\\s+)?${escape(role)}\\s+(?:of|from)\\s+${escape(locality)}(?=\\s|[.,:;]|$)|${escape(locality)}\\s+${escape(role)}\\s+${actor}(?=\\s|[.,:;]|$))`, 'i').test(key(text)));
}

const uncertain = /\b(?:assum(?:e|ed|ption)|hypothetical|anticipated|expected|planned|scheduled|would|could|might|pending confirmation)\b|\bmay\s+(?:be|support|oppose|express|attend|object|endorse)\b/i;

/** Source date, never retrieval time. Only complete, calendar-valid dates. */
function eventDate(text: string): string | undefined {
  const months = ['january','february','march','april','may','june','july','august','september','october','november','december'];
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text.trim());
  const named = /^(?:(\d{1,2})\s+([A-Za-z]+)|([A-Za-z]+)\s+(\d{1,2}),?)\s+(\d{4})$/.exec(text.trim());
  const year = Number(iso?.[1] ?? named?.[5]);
  const month = iso ? Number(iso[2]) : months.findIndex(name => name === (named?.[2] ?? named?.[3] ?? '').toLowerCase() || name.slice(0,3) === (named?.[2] ?? named?.[3] ?? '').toLowerCase()) + 1;
  const day = Number(iso?.[3] ?? named?.[1] ?? named?.[4]);
  if (!Number.isInteger(year) || year < 1900 || year > 2200 || month < 1 || month > 12 || day < 1) return undefined;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date.toISOString().slice(0,10) : undefined;
}

function completeCoverage(coverage: NonNullable<SiteDocument['topics']>['coverage'], body: string): coverage is NonNullable<NonNullable<SiteDocument['topics']>['coverage']> {
  if (!coverage || coverage.bodyHash !== sha256Hex(body) || coverage.segmentsTruncated
    || !Array.isArray(coverage.windows) || !coverage.windows.length
    || coverage.windows.some(window => !window || typeof window !== 'object')) return false;
  let end = 0;
  for (const window of [...coverage.windows].sort((a,b) => a.start - b.start)) {
    if (!window || window.status !== 'complete' || !Number.isInteger(window.start) || !Number.isInteger(window.end)
      || window.start < 0 || window.start > end || window.end <= window.start || window.end > body.length) return false;
    end = Math.max(end,window.end);
  }
  return end === body.length;
}

/** Stable source identity excludes retrieval time. A new/revoked/changed document
 * observation cannot reuse a scan of the previous admitted rows. */
export function documentedSentimentIdentity(entries: readonly PublicSentimentEntry[]): string {
  return hashCanonicalJson(entries.filter(row => row.sourceId === 'uploaded-document').map(row => ({
    url: row.url, btmTech: row.btmTech, sourcePassage: row.sourcePassage, observedDate: row.observedDate,
    sourceDateQuote: row.sourceDateQuote, documentSource: row.documentSource,
    sentimentScore: row.sentimentScore, confidence: row.confidence,
  })).sort((a,b) => a.url.localeCompare(b.url)));
}

/** Frozen report/decision projection: doc anchors require the retained source
 * witness; arbitrary doc URLs cannot inherit an assessed local score. */
export function documentedSentimentEntryIsSourceBound(entry: PublicSentimentEntry, context: SentimentQueryContext): boolean {
  const source = entry.documentSource;
  const text = entry.sourcePassage;
  const attribution = source?.attribution;
  if (entry.sourceId !== 'uploaded-document' || !source || !attribution || typeof text !== 'string' || !text
    || !source.documentId || !SHA.test(source.originalFileSha256) || !SHA.test(source.bodySha256)
    || !Number.isInteger(source.charStart) || !Number.isInteger(source.charEnd) || source.charStart < 0
    || source.charEnd <= source.charStart || source.charEnd - source.charStart !== text.length
    || attribution.appliesToProject === 'other-project' || !attribution.statedBy || !attribution.statedByRole
    || !publicRole.test(attribution.statedByRole) || !attribution.statedOn || eventDate(attribution.statedOn) !== entry.observedDate
    || entry.dateBasis !== 'event' || entry.sourceDateQuote !== attribution.statedOn
    || uncertain.test(text) || !namesCurrentProject(text,context.projectName)
    || !key(text).includes(key(attribution.statedBy)) || !key(text).includes(key(attribution.statedByRole))
    || !key(text).includes(key(attribution.statedOn)) || !namesLocalActor(text,attribution,context.localityTerms)
    || !context.localityTerms.some(locality => key(locality).length >= 3 && key(text).includes(key(locality)))) return false;
  if (entry.url !== `doc://${encodeURIComponent(source.documentId)}#start=${source.charStart}&end=${source.charEnd}&tech=${entry.btmTech}`) return false;
  if (source.demonstrationProvenance) {
    const parsed = demonstrationDocumentProvenanceV1Schema.safeParse(source.demonstrationProvenance);
    if (!parsed.success || parsed.data.bodySha256 !== source.bodySha256 || parsed.data.originalFileSha256 !== source.originalFileSha256) return false;
  }
  return true;
}

/** Keep the entire source paragraph; never clip a qualification or opposing follow-up. */
function paragraph(body: string, start: number, end: number): { text: string; start: number; end: number } | undefined {
  let from = 0, to = body.length;
  for (const boundary of body.matchAll(/\r?\n[ \t]*\r?\n/g)) {
    const at = boundary.index!;
    if (at + boundary[0].length <= start) from = at + boundary[0].length;
    else if (at >= end) { to = at; break; }
  }
  const raw = body.slice(from,to);
  if (!raw.trim() || raw.length > 5000) return undefined;
  return { text: raw, start: from, end: to };
}

/** Normal PDF/text evidence, distinct from a public API observation. The existing
 * scope/stance projection still decides whether the observation is measurable. */
export function documentedLocalSentimentEntries(args: {
  site: SiteEntity; sed: SiteEvidenceDocument; documents: readonly SiteDocument[];
  context: SentimentQueryContext; now: string;
}): PublicSentimentEntry[] {
  const eligible = new Map(docsEligibleForEvidence(args.site).map(doc => [doc.id,doc]));
  const rows: PublicSentimentEntry[] = [];
  const seen = new Set<string>();
  for (const doc of args.documents) {
    const canonical = eligible.get(doc.id);
    if (!canonical || canonical.contentHash !== doc.contentHash || canonical.storagePath !== doc.storagePath
      || !SHA.test(doc.contentHash ?? '') || !Array.isArray(args.sed.uploadedDocuments)
      || !args.sed.uploadedDocuments.some(row => row.docId === doc.id)) continue;
    const body = documentBodyText(doc);
    const coverage = canonical.topics?.coverage;
    if (!body || !completeCoverage(coverage,body)) continue;
    if (doc.demonstrationProvenance) {
      const source = demonstrationDocumentProvenanceV1Schema.safeParse(doc.demonstrationProvenance);
      if (!source.success || source.data.bodySha256 !== coverage.bodyHash || source.data.originalFileSha256 !== doc.contentHash
        || source.data.scenarioId !== args.site.demonstrationProvenance?.scenarioId) continue;
    }
    for (const fact of collectSedDocumentFacts([doc])) {
      const attribution = fact.attribution;
      if (fact.channel !== 'document' || !attribution || attribution.appliesToProject === 'other-project'
        || !args.site.name || (attribution.projectName && key(attribution.projectName) !== key(args.site.name))
        || !attribution.statedBy || !attribution.statedByRole || !publicRole.test(attribution.statedByRole)
        || !attribution.statedOn || !Number.isInteger(fact.charStart) || !Number.isInteger(fact.charEnd)
        || fact.charStart < 0 || fact.charEnd > body.length || fact.charEnd <= fact.charStart
        || body.slice(fact.charStart,fact.charEnd) !== fact.sourceQuote) continue;
      const source = paragraph(body,fact.charStart,fact.charEnd);
      const date = eventDate(attribution.statedOn);
      if (!source || !date || uncertain.test(source.text)
        || !key(source.text).includes(key(attribution.statedBy))
        || !key(source.text).includes(key(attribution.statedByRole))
        || !namesCurrentProject(source.text,args.site.name)
        || !key(source.text).includes(key(attribution.statedOn)) || !namesLocalActor(source.text,attribution,args.context.localityTerms)
        || !args.context.localityTerms.some(locality => key(locality).length >= 3 && key(source.text).includes(key(locality)))) continue;
      // A reviewed extractor rejection stays a rejection, including after reload.
      if ((args.site.siteContext?.claims ?? []).some(claim => claim.state === 'mis-extracted'
        && claim.sourceDocId === doc.id && (claim.sourceOffset
          ? claim.sourceOffset.start < source.end && claim.sourceOffset.end > source.start
          : !!claim.sourceSpan && source.text.includes(claim.sourceSpan)))) continue;
      for (const btmTech of args.context.techs) {
        const url = `doc://${encodeURIComponent(doc.id)}#start=${source.start}&end=${source.end}&tech=${btmTech}`;
        const identity = `${url}:${btmTech}`;
        if (seen.has(identity)) continue;
        const entry = scopeSentimentEntry({ sourceId: 'uploaded-document', fetchedAt: args.now, btmTech,
          sourcePassage: source.text, supportingSnippet: source.text.slice(0,280), passageTruncated: false,
          observedDate: date, sourceDateQuote: attribution.statedOn, dateBasis: 'event',
          scopeBasis: 'named-locality', url, sentimentScore: explicitProjectStance(source.text) ?? 0,
          confidence: Math.max(0,Math.min(fact.confidence,0.7)),
          documentSource: { documentId: doc.id, originalFileSha256: doc.contentHash!, bodySha256: coverage.bodyHash,
            charStart: source.start, charEnd: source.end, attribution,
            ...(doc.demonstrationProvenance ? { demonstrationProvenance: doc.demonstrationProvenance } : {}) },
        },args.context,args.now);
        if (entry.assessmentStatus !== 'assessed' || entry.scope !== 'site') continue;
        seen.add(identity); rows.push(entry);
      }
    }
  }
  return rows;
}
